"use client";

import { createContext, useContext } from "react";
import type { SelectOption } from "../../Select";
import type { EditableCellType } from "./EditableTableCell";

export type EditingCell = { rowId: string; colIndex: number } | null;

export type CellKey = `${string}:${number}`;

export function makeCellKey(rowId: string, colIndex: number): CellKey {
  return `${rowId}:${colIndex}`;
}

export function parseCellKey(key: CellKey): { rowId: string; colIndex: number } {
  const parts = key.split(":");
  return { rowId: parts.slice(0, -1).join(":"), colIndex: parseInt(parts[parts.length - 1], 10) };
}

export type CellRegistration = {
  value: string | number | boolean;
  type: EditableCellType;
  onCommit: (value: string | number | boolean) => void;
  options?: SelectOption<string>[];
  currencySymbol?: string;
};

export type EditingContextValue = {
  editingCell: EditingCell;
  /** When editing was triggered by a keypress (type-to-edit), this is the initial character. */
  editingInitialChar: string | null;
  startEdit: (rowId: string, colIndex: number, initialChar?: string) => void;
  cancelEdit: () => void;
  /** After confirming a cell edit with Enter: column-major advance (next row, same column).
   *  Shift+Enter reverses direction (prev row, same column).
   *  At row boundaries wraps to the next/prev selectable column; at table boundaries wraps around. */
  advanceCell: (rowId: string, colIndex: number, reverse?: boolean) => void;
  /** Tab / Shift+Tab navigation: row-major advance (next/prev selectable column, same row).
   *  At row boundaries wraps to the first/last column of the next/prev row.
   *  At table boundaries wraps around to the opposite corner. */
  tabCell: (rowId: string, colIndex: number, reverse?: boolean) => void;
  // Selection
  selectedCells: Set<CellKey>;
  selectCell: (rowId: string, colIndex: number, metaKey: boolean, shiftKey?: boolean) => void;
  clearSelection: () => void;
  isCellSelected: (rowId: string, colIndex: number) => boolean;
  // Cell registry
  registerCell: (rowId: string, colIndex: number, reg: CellRegistration) => void;
  unregisterCell: (rowId: string, colIndex: number) => void;
  getCellRegistration: (key: CellKey) => CellRegistration | undefined;
};

const emptySet = new Set<CellKey>();

export const EditingContext = createContext<EditingContextValue>({
  editingCell: null,
  editingInitialChar: null,
  startEdit: () => {},
  cancelEdit: () => {},
  advanceCell: () => {},
  tabCell: () => {},
  selectedCells: emptySet,
  selectCell: () => {},
  clearSelection: () => {},
  isCellSelected: () => false,
  registerCell: () => {},
  unregisterCell: () => {},
  getCellRegistration: () => undefined,
});

export const useEditingContext = () => useContext(EditingContext);

// Provides rowId + colIndex to child cells automatically from the table wrapper
export const CellPositionContext = createContext<{ rowId: string; colIndex: number }>({
  rowId: "",
  colIndex: -1,
});

export const useCellPosition = () => useContext(CellPositionContext);
